// Step-up re-authentication hook for sensitive actions (zero trust).
//
// The backend gates high-impact actions (link brokerage, delete account) behind a recent
// authentication. When such a call returns the step_up_required challenge, useStepUp
// prompts the user to re-authenticate (password + MFA), refreshing auth_time, then retries
// the action once. Daily read-only use is unaffected.

import { useCallback, useRef, useState } from "react";

import { StepUpDialog } from "./StepUpDialog";
import { isStepUpRequired } from "../api/client";

interface StepUpState {
  open: boolean;
  // Resolver for the in-flight step-up; completed when re-auth succeeds or is cancelled.
  resolve: ((ok: boolean) => void) | null;
}

export function useStepUp() {
  const [state, setState] = useState<StepUpState>({ open: false, resolve: null });
  const stateRef = useRef(state);
  stateRef.current = state;

  const promptStepUp = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        setState({ open: true, resolve });
      }),
    [],
  );

  const finish = useCallback((ok: boolean) => {
    stateRef.current.resolve?.(ok);
    setState({ open: false, resolve: null });
  }, []);

  /**
   * Run a sensitive action. If the backend demands step-up, prompt for re-auth and retry
   * once. Returns the action's result, or throws if it still fails after re-auth.
   */
  const runWithStepUp = useCallback(
    async <T,>(action: () => Promise<T>): Promise<T> => {
      try {
        return await action();
      } catch (err) {
        if (!isStepUpRequired(err)) throw err;
        const ok = await promptStepUp();
        if (!ok) throw err; // user cancelled; surface the original challenge
        return await action(); // retry once with the refreshed session
      }
    },
    [promptStepUp],
  );

  const dialog = <StepUpDialog open={state.open} onDone={finish} />;

  return { runWithStepUp, dialog };
}
